import { Experience } from '@/types'
import { useTranslation } from 'next-i18next'
import { Button } from '@/components/ui/button'
import Link from 'next/link'
import ExperienceOrganizer from '@/components/experiences/experience-organizer'
import { CldImage } from 'next-cloudinary'
import { Star } from 'lucide-react'
import { ContactMethod } from '@/lib/constants/contact-method'

type ContactLinkConfig = {
  hrefFor: (phone: string) => string
  labelKey: string
  target?: '_blank'
}

const CONTACT_LINK: Record<ContactMethod, ContactLinkConfig> = {
  [ContactMethod.WHATSAPP]: {
    hrefFor: (phone) =>
      `https://api.whatsapp.com/send/?phone=${phone}&text&type=phone_number&app_absent=0`,
    labelKey: 'whatsapp',
    target: '_blank',
  },
  [ContactMethod.PHONE]: {
    hrefFor: (phone) => `tel:${phone}`,
    labelKey: 'phone',
  },
}

const ExperienceContacts = ({
  experience,
  compact = false,
}: {
  experience: Experience
  compact?: boolean
}) => {
  const { t } = useTranslation('experience')

  if (compact) {
    return (
      <div className="mt-6 grid grid-cols-2 gap-4">
        <div>
          <h2 className="text-sm font-semibold">{t('organizer')}</h2>
          <div className="flex items-start space-x-3 mt-2">
            <CldImage
              src={experience.organizer.logo}
              width="48"
              height="48"
              className="rounded-md"
              alt={`logo of ${experience.organizer.name}`}
            />
            <div>
              <strong className="text-sm">{experience.organizer.name}</strong>
              {experience.organizer.rate ? (
                <div className="flex items-center">
                  <Star fill="#004125" size={12} strokeWidth={0} />
                  <span className="text-xs">
                    {experience.organizer.rate}/5
                  </span>
                </div>
              ) : null}
            </div>
          </div>
        </div>

        <div>
          <h2 className="text-sm font-semibold">{t('need_help?')}</h2>
          <p className="text-sm my-1">{t('contact_us')}</p>
          <ContactButtons experience={experience} size="sm" />
        </div>
      </div>
    )
  }

  return (
    <div>
      <h2 className="text-xl mt-6">{t('organizer')}</h2>

      <div className="grid grid-cols-1 lg:grid-cols-2">
        <div className="p-4">
          <ExperienceOrganizer company={experience.organizer} />
        </div>

        <div className="mt-4 md:mt-0">
          <h2 className="font-bold text-lg">{t('need_help?')}</h2>

          <div className="rounded-md">
            <h4 className="my-2">{t('contact_us')}</h4>
            <ContactButtons experience={experience} />
          </div>
        </div>
      </div>
    </div>
  )
}

const ContactButtons = ({
  experience,
  size,
}: {
  experience: Experience
  size?: 'sm' | 'default'
}) => {
  const { t } = useTranslation('experience')

  const contactLink = CONTACT_LINK[experience.contactMethod]

  return (
    <div className={size === 'sm' ? 'space-x-2' : 'space-x-4'}>
      {experience.phone && (
        <Link
          target={contactLink.target}
          href={contactLink.hrefFor(experience.phone)}
        >
          <Button
            type="button"
            variant="accent"
            size={size}
            className="capitalize"
          >
            {t(contactLink.labelKey, { ns: 'common' })}
          </Button>
        </Link>
      )}

      {experience.email && (
        <Link href={`mailto:${experience.email}`}>
          <Button className="capitalize" variant="accent" size={size}>
            {t('email', { ns: 'common' })}
          </Button>
        </Link>
      )}
    </div>
  )
}

export default ExperienceContacts
